import { supabase } from "@/lib/supabase";
import type { WorkspacePersona } from "@/config/workspacePersona";
import type { Database } from "@/types/database.types";

type AppRole = Database["public"]["Enums"]["app_role"];

export type ApplyPendingInviteReason =
  | "applied"
  | "already_accepted"
  | "conflict_existing_scope"
  | "not_authenticated"
  | "no_email_on_account"
  | "invalid_invite_reference"
  | "invite_not_found"
  | "email_mismatch"
  | "cancelled_invite"
  | "expired_invite";

export interface ApplyPendingInviteResult {
  applied: boolean;
  reason: ApplyPendingInviteReason;
  invite_id: string | null;
  school_id: string | null;
  district_id: string | null;
  workspace_persona: WorkspacePersona | null;
  role: AppRole | null;
}

class InviteAcceptanceService {
  /**
   * AE-218: apply only the invite referenced by the verified auth redirect.
   * The database validates the current authenticated email, status, and expiry
   * before it may create membership or mark the invite accepted.
   */
  applyPendingInvite = async (inviteId: string): Promise<ApplyPendingInviteResult> => {
    const { data, error } = await supabase.rpc("apply_pending_invite", { _invite_id: inviteId });
    if (error) throw error;
    const row = (data ?? [])[0];
    if (!row) {
      return {
        applied: false,
        reason: "invalid_invite_reference",
        invite_id: null,
        school_id: null,
        district_id: null,
        workspace_persona: null,
        role: null,
      };
    }
    return row as ApplyPendingInviteResult;
  };
}

export const inviteAcceptanceService = new InviteAcceptanceService();

export function isInviteAuthorizationDenied(result: ApplyPendingInviteResult): boolean {
  return !result.applied && result.reason !== "already_accepted" && result.reason !== "conflict_existing_scope";
}
